import React, { useEffect, useState } from 'react'
import { Award, Camera, Heart } from 'lucide-react'

export const AboutPage: React.FC = () => {
  const [businessInfo, setBusinessInfo] = useState<any>(null)

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await fetch('/data/business-info.json')
        const data = await response.json()
        setBusinessInfo(data)
      } catch (error) {
        console.error('Error fetching business info:', error)
      }
    }

    fetchBusinessInfo()
  }, [])

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
            About {businessInfo?.businessName || 'Duende Photo'}
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            {businessInfo?.tagline || 'Fine Art Wedding Photography'} serving the {businessInfo?.location || 'San Francisco Bay Area'} since {businessInfo?.established || '2005'}
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      {businessInfo?.philosophy && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 md:p-12 shadow-sm border border-gray-100">
              <div className="text-center mb-8">
                <Heart className="w-8 h-8 text-gray-600 mx-auto mb-4" />
                <h2 className="text-2xl font-light text-gray-900 mb-6">The Philosophy of Duende</h2>
              </div>
              <blockquote className="text-lg italic text-gray-700 leading-relaxed text-center mb-6">
                "{businessInfo.philosophy.quote}"
              </blockquote>
              <cite className="text-sm text-gray-500 text-center block">
                — {businessInfo.philosophy.author}
              </cite>
              <div className="mt-8 pt-8 border-t border-gray-100">
                <p className="text-gray-600 leading-relaxed">
                  The concept of "duende" embodies the soul, passion, and authentic emotion that transcends 
                  technical perfection. In our wedding photography, we seek to capture not just the visual 
                  beauty of your special day, but the raw, genuine emotions that make each moment unique 
                  and unforgettable.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* About Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-light text-gray-900 mb-6">Our Approach</h2>
              <p className="text-gray-600 leading-relaxed">
                With over {new Date().getFullYear() - parseInt(businessInfo?.established || '2005')} years 
                of experience in fine art wedding photography, we specialize in capturing the authentic 
                emotions and intimate moments that define your special day.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Our style blends classical composition with contemporary aesthetics, creating timeless 
                images that tell your unique love story. We believe in unobtrusive documentation, 
                allowing natural moments to unfold while subtly guiding you when needed.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Every couple is different, and we take pride in understanding your vision, preferences, 
                and the special details that make your wedding uniquely yours.
              </p>
            </div>

            <div className="bg-gray-50 p-8">
              <Camera className="w-12 h-12 text-gray-600 mb-6" />
              <h3 className="text-xl font-light text-gray-900 mb-4">Our Style</h3>
              <ul className="space-y-3 text-gray-600">
                <li>• Fine art wedding photography</li>
                <li>• Authentic emotional storytelling</li>
                <li>• Classical composition techniques</li>
                <li>• Natural light expertise</li>
                <li>• Unobtrusive documentation</li>
                <li>• Timeless aesthetic approach</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      {businessInfo?.services && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-light text-gray-900 mb-12 text-center">Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {businessInfo.services.map((service: string, index: number) => (
                <div key={index} className="text-center p-6 bg-white shadow-sm">
                  <h3 className="text-lg font-light text-gray-900 mb-2">{service}</h3>
                  <p className="text-sm text-gray-600">
                    Professional photography services tailored to your needs
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Service Areas */}
      {businessInfo?.serviceAreas && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-light text-gray-900 mb-8">Service Areas</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              We proudly serve couples throughout Northern California, with extensive experience 
              in the region's most beautiful venues.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {businessInfo.serviceAreas.map((area: string, index: number) => (
                <span key={index} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm tracking-wide">
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Awards Section */}
      {businessInfo?.awards && businessInfo.awards.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto text-center">
            <Award className="w-8 h-8 text-gray-600 mx-auto mb-6" />
            <h2 className="text-3xl font-light text-gray-900 mb-8">Recognition</h2>
            <div className="space-y-4">
              {businessInfo.awards.map((award: any, index: number) => (
                <div key={index} className="bg-white p-6 shadow-sm">
                  <h3 className="text-lg font-light text-gray-900 mb-1">{award.title}</h3>
                  <p className="text-sm text-gray-600">{award.organization}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
